/**
 * Demo Guide Page
 * 
 * Interactive demo guide for showcasing the Hospice Operating System features.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Brain, Activity, ClipboardCheck, Users, Bell, Settings,
  Play, ChevronRight, ExternalLink, Copy, Check, Star,
  Sparkles, BarChart3, Heart, Shield, Calendar, FileText,
  Stethoscope, Building, Video, MessageSquare, Lock
} from "lucide-react";
import { toast } from "sonner";

const DEMO_CREDENTIALS = [
  { role: "Administrator", email: "admin_test@goodwincares.com", password: "Test1234!" },
  { role: "Staff", email: "staff_test@goodwincares.com", password: "Test1234!" },
  { role: "Physician", email: "physician_test@goodwincares.com", password: "Test1234!" }
];

const DEMO_SECTIONS = [
  {
    id: "ai-predictions",
    title: "AI Predictive Care Analytics",
    icon: Brain,
    color: "text-violet-400",
    bgColor: "bg-violet-500/20",
    badge: "NEW",
    badgeColor: "bg-violet-500",
    route: "/hos",
    description: "AI-driven predictions for hospice eligibility based on functional decline trends",
    highlights: [
      "Risk classification: Critical, High, Moderate, Low",
      "Linear regression analysis of KPS/PPS decline",
      "Days-to-threshold predictions",
      "AI-generated care recommendations"
    ],
    demoSteps: [
      "Navigate to HOS Dashboard (/hos)",
      "Look for 'AI PREDICTIONS' widget in sidebar",
      "Note: Critical: 4 patients flagged",
      "Click 'View All Predictions' to expand",
      "Click any patient for trajectory analysis"
    ]
  },
  {
    id: "census-eligibility",
    title: "Census Eligibility Dashboard",
    icon: BarChart3,
    color: "text-blue-400",
    bgColor: "bg-blue-500/20",
    route: "/hos",
    description: "At-a-glance view of census-wide eligibility status",
    highlights: [
      "Donut chart showing patient distribution",
      "Average KPS score indicator",
      "Active alerts count",
      "Rapid decline tracking"
    ],
    demoSteps: [
      "Navigate to HOS Dashboard (/hos)",
      "View 'Census Eligibility Overview' at top",
      "Note the donut chart segments",
      "Check Average KPS: 68 (amber threshold)",
      "Review Rapid Decline: 4 patients"
    ]
  },
  {
    id: "lcd-checker",
    title: "LCD Eligibility Checker",
    icon: ClipboardCheck,
    color: "text-emerald-400",
    bgColor: "bg-emerald-500/20",
    route: "/lcd-eligibility",
    description: "Comprehensive Local Coverage Determination criteria checking",
    highlights: [
      "10 diagnosis categories (Cancer, Dementia, Heart, etc.)",
      "Functional assessment sliders (KPS/PPS)",
      "Patient history integration",
      "Eligibility threshold visualization"
    ],
    demoSteps: [
      "Navigate to LCD Eligibility (/lcd-eligibility)",
      "Enter patient name and ID",
      "Select a diagnosis category",
      "Adjust KPS/PPS sliders",
      "View Patient Functional History in sidebar"
    ]
  },
  {
    id: "smart-documentation",
    title: "Smart Documentation",
    icon: FileText,
    color: "text-amber-400",
    bgColor: "bg-amber-500/20",
    route: "/hos",
    description: "AI-powered clinical documentation with GPT-5.2",
    highlights: [
      "Voice-to-text with OpenAI Whisper",
      "AI suggestions for clinical notes",
      "Compliance checking in real-time",
      "Template library"
    ],
    demoSteps: [
      "Open Command Palette (⌘K)",
      "Search for 'Smart Documentation'",
      "Use voice recording for notes",
      "Review AI-generated suggestions"
    ]
  },
  {
    id: "ehr-interop",
    title: "EHR Interoperability",
    icon: Building,
    color: "text-cyan-400",
    bgColor: "bg-cyan-500/20",
    route: "/ehr-config",
    description: "Connect with external EHR systems",
    highlights: [
      "Epic, Cerner, Meditech, Allscripts support",
      "FHIR R4 and HL7 ADT protocols",
      "Bi-directional data sync",
      "Connection status monitoring"
    ],
    demoSteps: [
      "Navigate to EHR Config (/ehr-config)",
      "View supported EHR systems",
      "Review connection protocols",
      "Check sync status indicators"
    ]
  },
  {
    id: "telehealth",
    title: "Telehealth Center",
    icon: Video,
    color: "text-pink-400",
    bgColor: "bg-pink-500/20",
    route: "/telehealth-center",
    description: "Virtual visits and remote patient monitoring",
    highlights: [
      "Video consultations",
      "Remote vital monitoring",
      "Family conference calls",
      "Recording and transcription"
    ],
    demoSteps: [
      "Navigate to Telehealth (/telehealth-center)",
      "View scheduled virtual visits",
      "Start a demo video session"
    ]
  }
];

const KEY_URLS = [
  { name: "Login", path: "/login", icon: Lock },
  { name: "HOS Dashboard", path: "/hos", icon: Activity },
  { name: "LCD Eligibility", path: "/lcd-eligibility", icon: ClipboardCheck },
  { name: "EHR Config", path: "/ehr-config", icon: Building },
  { name: "Admin Dashboard", path: "/admin", icon: Settings },
  { name: "Compliance", path: "/compliance", icon: Shield },
  { name: "Staff Dashboard", path: "/staff", icon: Users },
  { name: "Executive Dashboard", path: "/executive-dashboard", icon: BarChart3 },
  { name: "Telehealth", path: "/telehealth-center", icon: Video },
  { name: "Volunteer Portal", path: "/volunteer-portal", icon: Heart }
];

export default function DemoGuidePage() {
  const navigate = useNavigate();
  const [copiedCredential, setCopiedCredential] = useState(null);
  const [activeSection, setActiveSection] = useState("ai-predictions");

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCredential(id);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedCredential(null), 2000);
  };

  const activeDemo = DEMO_SECTIONS.find(s => s.id === activeSection);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">GoodwinCare Demo Guide</h1>
                <p className="text-xs text-slate-400">Hospice Operating System</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                <Sparkles className="w-3 h-3 mr-1" />
                Live Demo
              </Badge>
              <Button 
                onClick={() => navigate("/login")}
                className="bg-teal-600 hover:bg-teal-700"
              >
                <Play className="w-4 h-4 mr-2" />
                Start Demo
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Test Credentials Section */}
        <Card className="bg-slate-900/50 border-slate-800 mb-8">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-white">
              <Lock className="w-5 h-5 text-amber-400" />
              Demo Credentials
            </CardTitle>
            <CardDescription>
              Use these test accounts to explore different user roles
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {DEMO_CREDENTIALS.map((cred, idx) => (
                <div 
                  key={idx}
                  className="p-4 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-slate-600 transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <Badge variant="outline" className="text-slate-300">
                      {cred.role}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(`${cred.email}\n${cred.password}`, idx)}
                      className="h-7 px-2"
                    >
                      {copiedCredential === idx ? (
                        <Check className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-slate-400" />
                      )}
                    </Button>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-slate-300 font-mono">{cred.email}</p>
                    <p className="text-sm text-slate-500 font-mono">{cred.password}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Main Demo Content */}
        <div className="grid grid-cols-12 gap-6">
          {/* Feature List Sidebar */}
          <div className="col-span-12 lg:col-span-4">
            <Card className="bg-slate-900/50 border-slate-800 sticky top-24">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-white">Demo Features</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <div className="p-4 pt-0 space-y-2">
                    {DEMO_SECTIONS.map((section) => {
                      const Icon = section.icon;
                      const isActive = activeSection === section.id;
                      return (
                        <button
                          key={section.id}
                          onClick={() => setActiveSection(section.id)}
                          className={`w-full p-3 rounded-lg text-left transition-all ${
                            isActive 
                              ? "bg-slate-800 border border-slate-700" 
                              : "hover:bg-slate-800/50"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg ${section.bgColor} flex items-center justify-center`}>
                              <Icon className={`w-4 h-4 ${section.color}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className={`text-sm font-medium truncate ${isActive ? "text-white" : "text-slate-300"}`}>
                                  {section.title}
                                </p>
                                {section.badge && (
                                  <Badge className={`${section.badgeColor} text-white text-[10px] px-1.5 py-0`}>
                                    {section.badge}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <ChevronRight className={`w-4 h-4 ${isActive ? "text-white" : "text-slate-500"}`} />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Feature Detail */}
          <div className="col-span-12 lg:col-span-8">
            {activeDemo && (
              <Card className="bg-slate-900/50 border-slate-800">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl ${activeDemo.bgColor} flex items-center justify-center`}>
                        <activeDemo.icon className={`w-6 h-6 ${activeDemo.color}`} />
                      </div>
                      <div>
                        <CardTitle className="text-xl text-white flex items-center gap-2">
                          {activeDemo.title}
                          {activeDemo.badge && (
                            <Badge className={`${activeDemo.badgeColor} text-white`}>
                              {activeDemo.badge}
                            </Badge>
                          )}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {activeDemo.description}
                        </CardDescription>
                      </div>
                    </div>
                    <Button 
                      onClick={() => navigate(activeDemo.route)}
                      className="bg-teal-600 hover:bg-teal-700"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open Feature
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Highlights */}
                  <div>
                    <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Star className="w-4 h-4 text-amber-400" />
                      Key Highlights
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {activeDemo.highlights.map((highlight, idx) => (
                        <div 
                          key={idx}
                          className="p-3 rounded-lg bg-slate-800/50 border border-slate-700"
                        >
                          <p className="text-sm text-slate-300">{highlight}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Demo Steps */}
                  <div>
                    <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Play className="w-4 h-4 text-emerald-400" />
                      Demo Walkthrough
                    </h3>
                    <div className="space-y-2">
                      {activeDemo.demoSteps.map((step, idx) => (
                        <div 
                          key={idx}
                          className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/30"
                        >
                          <div className="w-6 h-6 rounded-full bg-teal-500/20 text-teal-400 flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {idx + 1}
                          </div>
                          <p className="text-sm text-slate-300">{step}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Demo Script */}
                  <div className="p-4 rounded-lg bg-violet-500/10 border border-violet-500/30">
                    <h4 className="text-sm font-semibold text-violet-300 mb-2 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Demo Script
                    </h4>
                    <p className="text-sm text-slate-300 italic">
                      {activeDemo.id === "ai-predictions" && (
                        "\"Our AI analyzes functional decline trends to predict when patients will cross hospice eligibility thresholds. The system flagged John Smith as Critical - predicting he'll reach threshold within days based on his KPS trajectory. The AI recommends immediate goals of care conversation.\""
                      )}
                      {activeDemo.id === "census-eligibility" && (
                        "\"This gives administrators an at-a-glance view of census-wide eligibility. Four patients have 'rapid decline' status - meaning their functional scores dropped 20+ points in 14 days.\""
                      )}
                      {activeDemo.id === "lcd-checker" && (
                        "\"When a clinician enters a patient ID, they immediately see that patient's functional history - including trend charts and the AI's threshold prediction. This context helps them make informed eligibility decisions.\""
                      )}
                      {activeDemo.id === "smart-documentation" && (
                        "\"Our GPT-5.2 powered documentation system lets clinicians dictate notes using voice, with AI suggestions for clinical terminology and compliance checking in real-time.\""
                      )}
                      {activeDemo.id === "ehr-interop" && (
                        "\"We support bi-directional data sync with major EHR systems including Epic, Cerner, and Meditech using FHIR R4 and HL7 protocols.\""
                      )}
                      {activeDemo.id === "telehealth" && (
                        "\"Our telehealth center enables virtual visits with patients and families, including recording and transcription capabilities.\""
                      )}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Links */}
            <Card className="bg-slate-900/50 border-slate-800 mt-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-white">Quick Navigation</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {KEY_URLS.map((url, idx) => {
                    const Icon = url.icon;
                    return (
                      <Button
                        key={idx}
                        variant="outline"
                        className="h-auto py-3 flex-col gap-2 bg-slate-800/30 border-slate-700 hover:bg-slate-800 hover:border-slate-600"
                        onClick={() => navigate(url.path)}
                      >
                        <Icon className="w-5 h-5 text-slate-400" />
                        <span className="text-xs text-slate-300">{url.name}</span>
                      </Button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Mocked Services Notice */}
        <Card className="bg-amber-500/10 border-amber-500/30 mt-8">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-amber-300 mb-1">Mocked Services Notice</h4>
                <p className="text-sm text-slate-300">
                  The following services are mocked in this demo environment:
                  <span className="text-amber-400 font-medium"> E-Prescribing (Rcopia)</span>.
                  All other integrations including SMS (Twilio) and Email (Resend) are fully configured.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 mt-12 py-6">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm text-slate-500">
            GoodwinCare Hospice Operating System • Demo Guide • January 2026
          </p>
        </div>
      </footer>
    </div>
  );
}
